import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Notice, Skeleton, StageTracker } from '@sauda/ui/kit';
import { Screen } from '../../app/chrome.js';
import { ApiError, api } from '../../lib/api.js';
import {
  basicsProblems,
  draftFromView,
  draftToBody,
  type BasicsProblems,
  type PropertyDraft,
} from '../../lib/property-form.js';
import type { PropertyView } from '../../lib/types.js';
import { BasicsFields, DetailsFields } from './FactsFields.js';

/** Edit every fact of a saved property in two steps. Photos are managed on the property itself. */
export function PropertyEditScreen() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const property = useQuery({
    queryKey: ['property', id],
    queryFn: () => api<PropertyView>(`/properties/${id}`),
  });
  if (property.data === undefined)
    return (
      <Screen title={t('property.edit')} back={`/properties/${id}`}>
        <Skeleton height={200} />
      </Screen>
    );
  return (
    <EditForm
      view={property.data}
      onSaved={async () => {
        await cache.invalidateQueries({ queryKey: ['property', id] });
        await cache.invalidateQueries({ queryKey: ['property-share', id] });
        await cache.invalidateQueries({ queryKey: ['properties'] });
        void navigate(`/properties/${id}`);
      }}
    />
  );
}

function EditForm({ view, onSaved }: { view: PropertyView; onSaved: () => Promise<void> }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<PropertyDraft>(() => draftFromView(view));
  const [step, setStep] = useState(0);
  const [problems, setProblems] = useState<BasicsProblems>({});
  const [error, setError] = useState<string | null>(null);
  const patch = (next: Partial<PropertyDraft>) => setDraft((d) => ({ ...d, ...next }));
  const save = useMutation({
    mutationFn: () => {
      const { kind: _kind, ...rest } = draftToBody(draft);
      return api<PropertyView>(`/properties/${view.id}`, { method: 'PATCH', body: rest });
    },
    onSuccess: onSaved,
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status === 403
          ? t('property.notAllowed')
          : t('property.generic'),
      ),
  });
  const stages = [
    { key: 'basics', label: t('property.stepBasics') },
    { key: 'details', label: t('property.stepDetails') },
  ];
  return (
    <Screen title={t('property.edit')} eyebrow={view.title} back={`/properties/${view.id}`}>
      <StageTracker stages={stages} current={step} label={t('property.steps')} />
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      {step === 0 ? <BasicsFields draft={draft} onChange={patch} problems={problems} /> : null}
      {step === 1 ? <DetailsFields draft={draft} onChange={patch} /> : null}
      <div className="step-bar">
        {step === 0 ? null : (
          <Button variant="text" size="lg" onClick={() => setStep(0)}>
            {t('app.back')}
          </Button>
        )}
        {step === 0 ? (
          <Button
            size="lg"
            onClick={() => {
              const found = basicsProblems(draft);
              setProblems(found);
              if (Object.keys(found).length === 0) setStep(1);
            }}
          >
            {t('property.next')}
          </Button>
        ) : (
          <Button size="lg" loading={save.isPending} onClick={() => save.mutate()}>
            {t('property.save')}
          </Button>
        )}
      </div>
    </Screen>
  );
}
